// CreditNova Report Schemas
// Used for report generation with Vercel AI SDK's generateObject()
import { z } from 'zod';
import {
  financialDataSchema,
  creditReportExtractedDataSchema,
  selectedCreditReportItemsSchema,
  accountDataSchema,
  anyBankStatementFindingsSchema,
} from './extraction.js';

// ===========================================
// Source Citation Schema
// ===========================================

export const sourceCitationSchema = z.object({
  documentType: z.enum([
    'financial_statement',
    'credit_report',
    'corporate_search',
    'bank_statement',
    'off_balance_sheet',
    'off_balance_sheet_items',
  ]),
  section: z.string(), // e.g., "Income Statement", "Credit Summary"
  textExcerpt: z.string(), // The actual text from the document
  pageReference: z.string().optional(), // Page number if available
  lineItems: z.array(z.string()).optional(), // Related line items used
  pageNumbers: z.array(z.number()).optional(), // Page numbers where found
});

export type SourceCitation = z.infer<typeof sourceCitationSchema>;

// ===========================================
// Financial Ratio Finding Schema
// ===========================================

export const ratioFindingSchema = z.object({
  ratioName: z.string(), // e.g., "Current Ratio"
  category: z.string(), // e.g., "Liquidity"
  calculatedValue: z.string(), // e.g., "2.5"
  formula: z.string(), // e.g., "Current Assets / Current Liabilities"
  calculation: z.string(), // e.g., "$500,000 / $200,000 = 2.5"
  interpretation: z.string(), // What this ratio means
  sources: z.array(sourceCitationSchema), // Where the data came from
});

export type RatioFinding = z.infer<typeof ratioFindingSchema>;

// ===========================================
// Credit Report Finding Schema
// ===========================================

export const creditReportFindingSchema = z.object({
  category: z.string(), // e.g., "Credit Summary"
  itemName: z.string(), // e.g., "Credit Score"
  value: z.string(), // The extracted value
  significance: z.string(), // What this means for credit analysis
  source: sourceCitationSchema,
});

export type CreditReportFinding = z.infer<typeof creditReportFindingSchema>;

// ===========================================
// Account Finding Schema
// ===========================================

export const accountFindingSchema = z.object({
  fieldName: z.string(), // e.g., "Year of Incorporation"
  value: z.string(), // The extracted value
  relevance: z.string(), // Why this matters for credit
  source: sourceCitationSchema,
});

export type AccountFinding = z.infer<typeof accountFindingSchema>;

// ===========================================
// Custom Ratio Schema
// ===========================================

export const customRatioSchema = z.object({
  id: z.string(), // UUID
  name: z.string(), // e.g., "Adjusted Current Ratio"
  formula: z.string(), // e.g., "Current Assets / Current Liabilities"
  description: z.string(), // Description of what this ratio measures
});

export type CustomRatio = z.infer<typeof customRatioSchema>;

// ===========================================
// Summary Preferences Schema
// ===========================================

export const summaryStyleSchema = z.enum(['comprehensive', 'concise', 'bullet-points', 'executive']);
export type SummaryStyle = z.infer<typeof summaryStyleSchema>;

export const summaryLengthSchema = z.enum(['brief', 'standard', 'detailed', 'extensive']);
export type SummaryLength = z.infer<typeof summaryLengthSchema>;

export const financialStatementTypeSchema = z.enum(['interim_fs', 'fye_accountant']);
export type FinancialStatementType = z.infer<typeof financialStatementTypeSchema>;

export const writeupTypeSchema = z.enum([
  'bank_statements', 
  'interim_fs', 
  'fye_accountant', 
  'full_analysis',
  'combined_financial_analysis', 
  'underwriting_summary',        
  'custom_credit_writeup'        
]);
export type WriteupType = z.infer<typeof writeupTypeSchema>;

export const summaryPreferencesSchema = z.object({
  selectedDataPoints: z.object({
    financialStatements: z.array(z.string()),
    creditReport: z.array(z.string()).optional(),
    metrics: z.array(z.string()),
    corporateSearch: z.array(z.string()),
    bankStatements: z.array(z.string()),
  }),
  summaryStyle: summaryStyleSchema,
  summaryLength: summaryLengthSchema,
  customInstructions: z.string(),
  bankStatementType: z.enum(['individual', 'business', 'dynamic_capital']).optional().default('individual'),
  financialStatementType: financialStatementTypeSchema.optional().default('interim_fs'),
  // Optional user-provided report name. When set, this takes priority over
  // names extracted from documents (e.g., company name from financial data).
  // If empty/undefined, the existing name-from-extraction fallback chain is used:
  //   financialData.companyName > creditReport.subjectName > bankStatement.accountHolder > corporate.companyName
  reportName: z.string().optional(),
  // When true, the research agent will perform external web searches using
  // business-identifying information (company name, principal names, industry,
  // location) extracted from uploaded documents. Requires explicit user consent
  // because this data is sent to third-party AI and search services.
  enableExternalResearch: z.boolean().optional().default(true),
  createdAt: z.string(),
});

export type SummaryPreferences = z.infer<typeof summaryPreferencesSchema>;

// ===========================================
// Findings Section Schema
// ===========================================

export const findingsSectionSchema = z.object({
  financialRatios: z.array(ratioFindingSchema),
  creditReportItems: z.array(creditReportFindingSchema).optional(),
  accountItems: z.array(accountFindingSchema),
  bankStatementFindings: anyBankStatementFindingsSchema.nullable().optional(),
  crossDocumentFindings: z.array(z.object({
    checkName: z.string(),
    result: z.string(),
    detail: z.string(),
    severity: z.string(),
  })).nullable().optional(),
  externalResearch: z.object({
    adverseFindings: z.array(z.string()),
    industryContext: z.string(),
    businessLegitimacy: z.string(),
  }).nullable().optional(),
  riskAssessment: z.object({
    overallRisk: z.enum(['low', 'moderate', 'elevated', 'high']),
    keyStrengths: z.array(z.string()),
    keyConcerns: z.array(z.string()),
    materialFlags: z.array(z.string()),
  }).nullable().optional(),
});

export type FindingsSection = z.infer<typeof findingsSectionSchema>;

// ===========================================
// Summary Section Schema
// ===========================================

export const summarySectionSchema = z.object({
  companyName: z.string(),
  executiveSummary: z.string(), // AI-generated overview
  accountSummary: z.object({
    yearOfIncorporation: z.string().nullable().optional(),
    ownershipStructure: z.string().nullable().optional(),
    operatingArea: z.string().nullable().optional(),
    numberOfLocations: z.string().nullable().optional(),
    servicesProvided: z.string().nullable().optional(),
    mainCustomers: z.string().nullable().optional(),
  }),
  dealSummary: z.object({
    transactionDetails: z.string(),
  }),
  writeupType: z.object({
    templateType: writeupTypeSchema,
    templateDescription: z.string(),
  }),
  generatedDate: z.string(),
});

export type SummarySection = z.infer<typeof summarySectionSchema>;

// ===========================================
// Complete Credit Report Schema
// ===========================================

export const creditReportSchema = z.object({
  reportId: z.string(),
  uploadId: z.string().optional(), // For accessing PDF page images
  summary: summarySectionSchema,
  findings: findingsSectionSchema,
  rawData: z.object({
    financialData: financialDataSchema.nullable().optional(),
    selectedMetrics: z.array(z.string()).optional(),
    customRatios: z.array(customRatioSchema).optional(),
    offBalanceSheetRecords: z.record(z.string(), z.any()).optional(),
    creditReportData: creditReportExtractedDataSchema.nullable().optional(),
    selectedCreditReportItems: selectedCreditReportItemsSchema.nullable().optional(),
    accountData: accountDataSchema.nullable().optional(),
    bankStatementFindings: anyBankStatementFindingsSchema.nullable().optional(),
    dealText: z.string().optional(),
  }),
});

export type CreditReport = z.infer<typeof creditReportSchema>;

// ===========================================
// Analysis Metadata Schema
// ===========================================

export const analysisMetadataSchema = z.object({
  reportId: z.string(),
  businessName: z.string(),
  generatedDate: z.string(),
  writeupType: writeupTypeSchema,
});

export type AnalysisMetadata = z.infer<typeof analysisMetadataSchema>;

// ===========================================
// Analysis Template Schema
// ===========================================

export const analysisTemplateSchema = z.object({
  id: z.string(), // UUID
  name: z.string(), // User-friendly template name
  description: z.string().optional(), // Optional description
  detailLevel: z.enum(['high-level', 'detailed', 'in-depth']),
  selectedMetrics: z.array(z.string()), // Financial ratios to calculate
  customRatios: z.array(customRatioSchema).optional(),
  selectedCreditReportCategories: z.array(z.string()).optional(),
  writeupType: writeupTypeSchema,
  summaryPreferences: summaryPreferencesSchema.optional(),
  bankStatementPreferences: z.object({
    accountHolder: z.string().optional(),
    institution: z.string().optional(),
    selectedCategories: z.array(z.string()).optional(),
  }).optional(),
  hasBankStatementAnalysis: z.boolean().optional(),
  bankStatementType: z.string().optional(),
  externalAccessible: z.boolean().optional(),
  createdDate: z.string(),
});

export type AnalysisTemplate = z.infer<typeof analysisTemplateSchema>;

export const insertTemplateSchema = analysisTemplateSchema.omit({ id: true, createdDate: true });
export type InsertTemplateInput = z.infer<typeof insertTemplateSchema>;

// ===========================================
// Report Generation Input Schema
// ===========================================

export const reportGenerationInputSchema = z.object({
  businessName: z.string(),
  writeupType: writeupTypeSchema,
  dealText: z.string().optional(),
  financialData: financialDataSchema.nullable().optional(),
  selectedMetrics: z.array(z.string()),
  customRatios: z.array(customRatioSchema).optional(),
  offBalanceSheetRecords: z.record(z.string(), z.any()).optional(),
  creditReportData: creditReportExtractedDataSchema.nullable().optional(),
  selectedCreditReportItems: selectedCreditReportItemsSchema.nullable().optional(),
  accountData: accountDataSchema.nullable().optional(),
  bankStatementFindings: anyBankStatementFindingsSchema.nullable().optional(),
  summaryPreferences: summaryPreferencesSchema.nullable().optional(),
});

export type ReportGenerationInput = z.infer<typeof reportGenerationInputSchema>;

// ===========================================
// Standard Financial Metrics
// ===========================================

export const FINANCIAL_METRICS = [
  // Liquidity Ratios
  { id: 'current_ratio', name: 'Current Ratio', category: 'Liquidity', formula: 'Current Assets / Current Liabilities' },
  { id: 'quick_ratio', name: 'Quick Ratio', category: 'Liquidity', formula: '(Current Assets - Inventory) / Current Liabilities' },
  { id: 'cash_ratio', name: 'Cash Ratio', category: 'Liquidity', formula: 'Cash / Current Liabilities' },
  { id: 'working_capital', name: 'Working Capital', category: 'Liquidity', formula: 'Current Assets - Current Liabilities' },

  // Profitability Ratios
  { id: 'gross_margin', name: 'Gross Profit Margin', category: 'Profitability', formula: 'Gross Profit / Revenue' },
  { id: 'operating_margin', name: 'Operating Margin', category: 'Profitability', formula: 'Operating Income / Revenue' },
  { id: 'net_margin', name: 'Net Profit Margin', category: 'Profitability', formula: 'Net Income / Revenue' },
  { id: 'roa', name: 'Return on Assets (ROA)', category: 'Profitability', formula: 'Net Income / Total Assets' },
  { id: 'roe', name: 'Return on Equity (ROE)', category: 'Profitability', formula: 'Net Income / Shareholders Equity' },

  // Leverage Ratios
  { id: 'debt_ratio', name: 'Debt Ratio', category: 'Leverage', formula: 'Total Debt / Total Assets' },
  { id: 'debt_equity', name: 'Debt to Equity', category: 'Leverage', formula: 'Total Debt / Total Equity' },
  { id: 'equity_multiplier', name: 'Equity Multiplier', category: 'Leverage', formula: 'Total Assets / Shareholders Equity' },
  { id: 'interest_coverage', name: 'Interest Coverage', category: 'Leverage', formula: 'EBIT / Interest Expense' },
  { id: 'dscr', name: 'Debt Service Coverage (DSCR)', category: 'Leverage', formula: 'EBITDA / Total Debt Service' },

  // Efficiency Ratios
  { id: 'asset_turnover', name: 'Asset Turnover', category: 'Efficiency', formula: 'Revenue / Total Assets' },
  { id: 'inventory_turnover', name: 'Inventory Turnover', category: 'Efficiency', formula: 'COGS / Average Inventory' },
  { id: 'receivables_turnover', name: 'Receivables Turnover', category: 'Efficiency', formula: 'Revenue / Average Receivables' },
  { id: 'dso', name: 'Days Sales Outstanding (DSO)', category: 'Efficiency', formula: '365 / Receivables Turnover' },
  { id: 'dio', name: 'Days Inventory Outstanding (DIO)', category: 'Efficiency', formula: '365 / Inventory Turnover' },

  // Additional Ratios
  { id: 'ebitda', name: 'EBITDA', category: 'Profitability', formula: 'Operating Income + Depreciation + Amortization' },
  { id: 'ebitdar', name: 'EBITDAR', category: 'Profitability', formula: 'EBITDA + Rent' },
] as const;

export type FinancialMetricId = (typeof FINANCIAL_METRICS)[number]['id'];

